import { useState, useCallback, useRef } from 'react';
import { doc, getDoc, collection, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

const OPERATOR_NAME_FIELDS = [
  'organizationName',
  'businessName',
  'operatorName',
  'orgName',
  'companyName',
  'firmName',
  'tradingName',
  'shopName',
  'storeName',
  'displayName',
  'name',
];

const firstNonEmptyString = (data, fields) => {
  for (const field of fields) {
    const value = data?.[field];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

export const useJREFetcher = () => {
  const [jreMap, setJreMap] = useState({});
  const [operatorMap, setOperatorMap] = useState({});
  const [courierMap, setCourierMap] = useState({});

  const fetchedJreRef = useRef(new Set());
  const fetchedOperatorRef = useRef(new Set());
  const fetchedCourierRef = useRef(new Set());

  const resolveJreName = (data, fallbackId) => {
    const fullName =
      data.fullName ||
      [data.firstName, data.lastName].filter(Boolean).join(' ').trim() ||
      '';
    return (
      data.jreName ||
      data.name ||
      fullName ||
      data.displayName ||
      data.organizationName ||
      data.businessName ||
      fallbackId
    );
  };

  // Resolves the operator's display name.
  // Deliberately returns `null` (never the operatorId) when no name field is
  // present: callers use `null` to mean "not resolved yet", whereas falling
  // back to the ID makes an ID indistinguishable from a real name and it then
  // gets rendered in place of the name.
  const resolveOperatorName = useCallback(
    (data) => firstNonEmptyString(data, OPERATOR_NAME_FIELDS),
    [],
  );

  const fetchJREData = useCallback(async (jreId) => {
    if (!jreId || jreId === 'No JRE' || fetchedJreRef.current.has(jreId)) return;
    fetchedJreRef.current.add(jreId);

    try {
      const jreDoc = await getDoc(doc(db, 'jreregistrations', jreId));
      if (jreDoc.exists()) {
        const data = jreDoc.data();
        const primaryMobile = (data.primaryMobile || data.mobile || data.contact || data.phoneNumber || '')
          .toString()
          .trim();
        const operatorNumber = (data.OpertorNumber || data.operatorNumber || data.operatorMobile || data.operatorContact || '')
          .toString()
          .trim();

        setJreMap(prev => ({
          ...prev,
          [jreId]: {
            primaryMobile: primaryMobile || null,
            operatorNumber: operatorNumber || null,
            name: resolveJreName(data, jreId),
          }
        }));
      } else {
        setJreMap(prev => ({
          ...prev,
          [jreId]: { primaryMobile: null, operatorNumber: null, name: jreId }
        }));
      }
    } catch (err) {
      console.error('Error fetching JRE:', jreId, err);
      setJreMap(prev => ({
        ...prev,
        [jreId]: { primaryMobile: null, operatorNumber: null, name: jreId }
      }));
    }
  }, []);

  const fetchOperatorData = useCallback(async (operatorId) => {
    if (!operatorId || operatorId === 'Unknown Operator' || fetchedOperatorRef.current.has(operatorId)) return;
    fetchedOperatorRef.current.add(operatorId);

    try {
      // Primary: operatorregistrations keyed by operatorId (e.g. OP-XXXX)
      const operatorDoc = await getDoc(doc(db, 'operatorregistrations', operatorId));
      if (operatorDoc.exists()) {
        const data = operatorDoc.data();
        const contact = (data.organizationContact || data.contact || data.mobile || data.transactionalMobile || data.phoneNumber || '')
          .toString()
          .trim();

        setOperatorMap(prev => ({
          ...prev,
          [operatorId]: {
            contact: contact || null,
            name: resolveOperatorName(data),
          }
        }));
        return;
      }

      // Fallback: some flows store the operator's MOBILE on the order instead
      // of the OP-XXXX id. `operator` docs are keyed by mobile.
      if (/^\d{10}$/.test((operatorId || '').toString())) {
        const opByMobile = await getDoc(doc(db, 'operator', operatorId));
        if (opByMobile.exists()) {
          const linkedId = opByMobile.data()?.operatorId;
          if (linkedId) {
            const linkedDoc = await getDoc(doc(db, 'operatorregistrations', linkedId));
            if (linkedDoc.exists()) {
              const data = linkedDoc.data();
              const contact = (data.organizationContact || data.contact || data.mobile || data.transactionalMobile || data.phoneNumber || '')
                .toString()
                .trim();
              setOperatorMap(prev => ({
                ...prev,
                [operatorId]: {
                  contact: contact || null,
                  name: resolveOperatorName(data),
                }
              }));
              return;
            }
          }
        }
      }

      // Fallback: OperatorSegmentRegistrations docs carry operatorId + mobile
      try {
        const segQ = query(
          collection(db, 'OperatorSegmentRegistrations'),
          where('operatorId', '==', operatorId),
        );
        const segSnap = await getDocs(segQ);
        if (!segSnap.empty) {
          const segData = segSnap.docs[0].data();
          const segMobile = (segData.mobile || '').toString();
          if (segMobile) {
            const opByMobile = await getDoc(doc(db, 'operator', segMobile));
            const linkedId = opByMobile.exists() ? opByMobile.data()?.operatorId : null;
            const lookupId = linkedId || operatorId;
            if (lookupId !== operatorId) {
              const linkedDoc = await getDoc(doc(db, 'operatorregistrations', lookupId));
              if (linkedDoc.exists()) {
                const data = linkedDoc.data();
                const contact = (data.organizationContact || data.contact || data.mobile || segMobile || '')
                  .toString()
                  .trim();
                setOperatorMap(prev => ({
                  ...prev,
                  [operatorId]: {
                    contact: contact || null,
                    name: resolveOperatorName(data),
                  }
                }));
                return;
              }
            }
          }
        }
      } catch {
        // fall through to ID fallback below
      }

      // No name could be resolved. Keep `name` null so the UI can show an
      // explicit placeholder instead of leaking the raw operator ID as a name.
      setOperatorMap(prev => ({
        ...prev,
        [operatorId]: { contact: null, name: null }
      }));
    } catch (err) {
      console.error('Error fetching Operator:', operatorId, err);
      setOperatorMap(prev => ({
        ...prev,
        [operatorId]: { contact: null, name: null }
      }));
    }
  }, [resolveOperatorName]);

  // Fetch courier branch details by branchId (stored as order.courierId).
  // Branch docs live at couriers/{courierId}/branches/{branchId}, so we
  // search via collectionGroup('branches') and derive the parent courierId.
  const fetchCourierData = useCallback(async (branchId) => {
    if (!branchId || fetchedCourierRef.current.has(branchId)) return;
    fetchedCourierRef.current.add(branchId);

    try {
      const q = query(collectionGroup(db, 'branches'), where('branchId', '==', branchId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const branchDoc = snap.docs[0];
        const branchData = branchDoc.data();
        const courierId = branchDoc.ref.parent.parent?.id || null;
        let courierName = branchData.courierName || null;
        let courierLogo = null;
        if (courierId) {
          try {
            const courierSnap = await getDoc(doc(db, 'couriers', courierId));
            if (courierSnap.exists()) {
              courierName = courierName || courierSnap.data().name || null;
              courierLogo = courierSnap.data().logo || null;
            }
          } catch {
            // keep branch-level values
          }
        }
        setCourierMap(prev => ({
          ...prev,
          [branchId]: {
            courierId,
            courierName,
            courierLogo,
            branchName: branchData.branchName || null,
            branchCode: branchData.branchCode || null,
            contactPerson: branchData.contactPerson || null,
            contactNumber: (branchData.contactNumber || '').toString().trim() || null,
            alternateContactNumber: (branchData.alternateContactNumber || '').toString().trim() || null,
            email: branchData.email || null,
            address: branchData.address || null,
          },
        }));
      } else {
        setCourierMap(prev => ({
          ...prev,
          [branchId]: { branchName: null, contactPerson: null, contactNumber: null },
        }));
      }
    } catch (err) {
      console.error('Error fetching courier branch:', branchId, err);
      setCourierMap(prev => ({
        ...prev,
        [branchId]: { branchName: null, contactPerson: null, contactNumber: null },
      }));
    }
  }, []);

  return {
    jreMap,
    operatorMap,
    courierMap,
    fetchJREData,
    fetchOperatorData,
    fetchCourierData,
  };
};
